import React from "react";
import { Helmet } from 'react-helmet-async'
import algoliasearch from 'algoliasearch/lite'
import { 
    InstantSearch, 
    SearchBox, 
    connectHits, 
    RefinementList, 
    Panel, 
    NumericMenu, 
    SortBy, 
    connectStateResults
} from 'react-instantsearch-dom'
import { Algolia, BASE_URL, IMG } from '../../constant'
import '../../sass/search.scss'
import { Link } from "react-router-dom";

// Initialize Algolia Search Client
const searchClient = algoliasearch(Algolia.ApplicationID, Algolia.SearchOnlyAPIKey);

// Calculate Discount Percentage
const getDiscountPercentage = (regularPrice, salePrice) => {
    if (regularPrice && salePrice && regularPrice > salePrice) {
        return ((regularPrice - salePrice) / regularPrice) * 100;
    }
    return 0;
};

// Get Lowest Sale Price from Variants
const getLowestSalePrice = (pro_var) => {
    if (pro_var && pro_var.length > 0) {
        const prices = pro_var.map((variant) => parseFloat(variant.sale_price || 0));
        return Math.min(...prices);
    }
    return null;
};

// Product Card Component
const RenderItem = ({ hit }) => {
    const discount = getDiscountPercentage(hit.regular_price, hit.sale_price);
    const lowestPrice = getLowestSalePrice(hit.ProVar);
    console.log('ff');
    console.log('Product:', hit.title, 'lowestPrice:', lowestPrice, 'ProVar:', hit.ProVar);

    return (
        <div className={`product-card ${hit.stock_status === 'Out of stock' ? 'out-of-stock' : ''}`}>
            <Link to={`/product/${hit.slug}`}>
                <img src={hit.thumbnail ? BASE_URL + hit.thumbnail : "https://via.placeholder.com/150"} alt={hit.title} className="product-image" />
                <h4 className="product-title">{hit.title}</h4>
                {lowestPrice !== null ? (
                    <div className="product-price">
                        <span className="price_regular">Starting @ <span className="price_sale">₹{lowestPrice}</span></span>
                    </div>
                ) : hit.sale_price ? (
                    <>
                        <div className="product-price">
                            <div className="priceRow">
                                <span className="price_regular">MRP </span> <span className="price_regular strike">₹{hit.regular_price}</span>
                            </div>
                            <div className="priceRow">
                                <span className="price_sale">₹{hit.sale_price}</span> <span className="price_discount">({discount.toFixed(0)}% OFF)</span>
                            </div>
                        </div>
                        <div className="discount-badge-wrapper">
                            <img src={IMG.tag} className="discount-badge" alt="discount-badge" />
                            <div className="discount-badge-text">{discount.toFixed(0)}% OFF</div>
                        </div>
                    </>
                ) : (
                    <div className="product-price">
                        <span className="price_regular">₹{hit.regular_price}</span>
                    </div>
                )}
                {/* <p className="stock-status">{hit.stock_status === "Out of stock" ? "Out of Stock" : "In Stock"}</p> */}
            </Link>
        </div>
    );
};

// Custom Hits Component (Product Listing)
const CustomHits = connectHits(({ hits }) => (
    <>
        {hits.length > 0 ?
            <div className="products-grid">
                {hits.map(hit => (
                    <RenderItem key={hit.objectID} hit={hit} />
                ))}
            </div>
        : <h6>Sorry, we couldn't find any results.</h6>}
    </>
));

// Custom Stats Component (Always Show Results Count)
const CustomStats = connectStateResults(({ searchResults, searching }) => {
    if (!searchResults || searching) return null;

    const { nbHits, query } = searchResults; // processingTimeMS,
    return (
        <div className="results-info">
            {query ? (
                <p className="results-info-count">Showing <b>{nbHits}</b> results for <b>"{query}"</b></p> // in {processingTimeMS}ms
            ) : (
                // <p>Showing {nbHits} products available</p>
                <p></p>
            )}
        </div>
    );
});

// Main Search Component
const SearchLarge = () => {  
    // Focus search input on page load
    const searchInputRef = React.useRef(null);
    React.useEffect(() => {
        if (searchInputRef.current) {
            // Algolia SearchBox renders an input inside a div, so we need to querySelector
            const input = searchInputRef.current.querySelector('input');
            if (input) input.focus();
        }
    }, []);

    return (
        <>
            <Helmet>
                <title>Search Dental Products Online | DentalStores.in</title>
                <meta name="description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta name="keywords" content="dental product search, buy dental instruments, best dental supplies, dental materials online, dental e-commerce" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://www.dentalstores.in/search" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.dentalstores.in/search" />
                <meta property="og:title" content="Search Dental Products Online | DentalStores.in" />
                <meta property="og:description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta property="og:image" content="https://www.dentalstores.in/logo.svg" />

                {/* Twitter Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Search Dental Products Online | DentalStores.in" />
                <meta name="twitter:description" content="Find the best dental products, instruments, and supplies at DentalStores.in. Search and buy high-quality dental materials with fast delivery across India." />
                <meta name="twitter:image" content="https://www.dentalstores.in/logo.svg" />
            </Helmet>
            <div className="search-container">
                <InstantSearch indexName={Algolia.IndexName} searchClient={searchClient}>
                    {/* Search Box */}
                    <div ref={searchInputRef}>
                        <SearchBox className="search-input" translations={{ placeholder: "Search for products, brands..." }} />
                    </div>

                    {/* Results Count */}
                    <div className="first-row">
                        <CustomStats />

                        <div className="sort-by-wrapper">
                            <p className="sort-by-text">Sort By Options:</p>
                            <SortBy
                                defaultRefinement={Algolia.IndexName}
                                items={[
                                    { value: Algolia.IndexName, label: "Relevance" },
                                    { value: `${Algolia.IndexName}_sale_price_asc`, label: "Price: Low to High" },
                                    { value: `${Algolia.IndexName}_sale_price_desc`, label: "Price: High to Low" }
                                ]}
                                onChange={(event) => console.log("Sort changed:", event.target.value)}
                            />
                        </div>
                    </div>
                    <div className='product_wrapper'>
                        <div className="filters-container">
                            {/* Stock Status Filter */}
                            <Panel header="Status">
                                <RefinementList attribute="stock_status" className="custom-refinement-list" />
                            </Panel>

                            {/* Categories Filter (With Search) */}
                            <Panel header="Categories">
                                <RefinementList 
                                    attribute="categories.name" 
                                    searchable={true} 
                                    translations={{ placeholder: "Search categories..." }}
                                    className="custom-refinement-list"
                                />
                            </Panel>

                            {/* Brands Filter (With Search & Show More) */}
                            <Panel header="Brands">
                                <RefinementList 
                                    attribute="brand"
                                    limit={5} 
                                    // showMore={true} 
                                    // showMoreLimit={10} 
                                    searchable={true} 
                                    translations={{ placeholder: "Search brands..." }}
                                    className="custom-refinement-list"
                                />
                            </Panel>

                            {/* Price Range Filter */}
                            <Panel header="Price Range">
                                <NumericMenu 
                                    attribute="sale_price"
                                    items={[
                                        { label: "Under ₹500", end: 500 },
                                        { label: "₹500 - ₹1000", start: 500, end: 1000 },
                                        { label: "₹1000 - ₹5000", start: 1000, end: 5000 },
                                        { label: "₹5000 - ₹10000", start: 5000, end: 10000 },
                                        { label: "Above ₹10000", start: 10000 }
                                    ]}
                                    className="custom-price-range"
                                />
                            </Panel>
                        </div>

                        {/* Product Listing */}
                        <CustomHits />
                    </div>

                </InstantSearch>
            </div>
        </>
    );
};

export default SearchLarge;
